import { apiFetch } from './api';

interface GeneratedSerial {
  serial: string;
}

/** The serial format the server should mint. */
export type SerialKind = 'robot' | 'software';

/**
 * The 2B distinction (#163) between the two entities the dashboard now keeps
 * apart: a **robot** serial (`RR-XXXX-XXXX-C`, or a legacy key) and a **software**
 * key (`XXXX-XXXX-XXXX-XXXX-C`).
 *
 * The two shapes are disjoint (14 vs 21 symbols), so the software shape alone
 * decides the kind — everything else, legacy keys included, is a robot. This is
 * the client twin of the server's SQL regex in
 * `rust/crates/ls-db/src/repo/dashboard.rs::list_licenses`; keep the two in sync.
 */
export function classifySerial(serial: string): SerialKind {
  return /^[0-9A-Z]{4}(-[0-9A-Z]{4}){3}-[0-9A-Z]$/.test(serial) ? 'software' : 'robot';
}

/**
 * Mint a fresh serial on the server (`POST /licenses/serial/generate`).
 *
 * The server owns both formats — the robot `RR-XXXX-XXXX-C` and the software key
 * `XXXX-XXXX-XXXX-XXXX-C` (`docs/serial-format.md`) — and checks the mint against
 * the unique serial index, so the SPA no longer fabricates a value in the browser.
 *
 * `kind` defaults to `'robot'` on the server, so calling this with no argument keeps
 * the historic robot request byte-compatible.
 */
export async function generateSerial(kind: SerialKind = 'robot'): Promise<string> {
  const resp = await apiFetch('/licenses/serial/generate', {
    method: 'POST',
    body: JSON.stringify({ kind }),
  });
  const data = (await resp.json()) as GeneratedSerial;
  return data.serial;
}
